import { useState, useEffect, useMemo, useRef } from "react"
import { useQueryState } from "nuqs"
import {
  useVideoLogs,
  useVideoLogById,
  useVideoLogRow,
  useVideoScanFilterOptions,
} from "../../hooks/useVideoLogs"
import ApiError from "../../components/ApiError"
import { getDateRange, groupByDate } from "../../utils/datetime"
import LogDetailPane from "./LogDetailPane"
import LogsSidebar from "./LogsSidebar"
import VideoLogsFilterBar from "./VideoLogsFilterBar"
import { parseMatchingVideos } from "./videoMetrics"

const PAGE_SIZE = 20

const EducationalVideoLogs = () => {
  const [searchTerm, setSearchTerm] = useState("")

  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: "All",
  })
  const [dateFrom, setDateFrom] = useQueryState("dateFrom", {
    defaultValue: "",
  })
  const [dateTo, setDateTo] = useQueryState("dateTo", { defaultValue: "" })
  const [status, setStatus] = useQueryState("status", { defaultValue: "All" })
  const [patientIssuer, setPatientIssuer] = useQueryState("patientIssuer", {
    defaultValue: "All",
  })
  // In the URL like every filter here, so a link to "this record" opens the
  // record the sender was actually looking at. The id rather than the accession
  // number: opaque, unique, and it keeps a patient-adjacent identifier out of
  // links pasted into a ticket.
  const [selectedLogId, setSelectedLogId] = useQueryState("logId", {
    defaultValue: "",
  })

  const sidebarScrollRef = useRef(null)
  const listRef = useRef(null)
  const detailScrollRef = useRef(null)
  const sentinelRef = useRef(null)

  const isSearchActive = !!activeSearchTerm
  const isCustomDateRange = dateRange === "Custom"
  const showCustomDateRange = !isSearchActive && isCustomDateRange

  const getDateFilterValue = (range) => {
    if (range === "All") return undefined
    const interval = getDateRange(range)
    return [interval.start.toISODate(), interval.end.toISODate()].join(",")
  }

  const getCustomDateFilterValue = () =>
    dateFrom || dateTo ? `${dateFrom},${dateTo}` : undefined

  const {
    data: { data, total } = { data: [], total: 0 },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideoLogs({
    limit: PAGE_SIZE,
    sortBy: "CreatedDate:DESC",
    filters: activeSearchTerm?.trim()
      ? { searchTerm: activeSearchTerm.trim() }
      : {
          timePeriod: isCustomDateRange
            ? getCustomDateFilterValue()
            : dateRange === "All"
              ? undefined
              : getDateFilterValue(dateRange),
          status: status === "All" ? undefined : status.toLowerCase(),
          patientIssuer: patientIssuer === "All" ? undefined : patientIssuer,
        },
  })

  const { data: filterOptions } = useVideoScanFilterOptions()
  const issuerOptions = ["All", ...(filterOptions?.patientIssuers || [])]

  /*
    The detail pane's own record, fetched directly by id — independent of
    `data` above. It does not matter whether this id is on a loaded page, an
    unloaded one, or excluded by the sidebar's current filters entirely: this
    is one request, keyed only on the id in the URL, so opening any record
    costs the same regardless of where — or whether — it sits in the list.
  */
  const { data: activeRow, isLoading: isRowLoading } =
    useVideoLogRow(selectedLogId)

  const { data: logDetail, isLoading: isDetailLoading } =
    useVideoLogById(selectedLogId)

  const videos = useMemo(
    () => parseMatchingVideos(activeRow?.MatchingVideos),
    [activeRow]
  )

  const groups = useMemo(
    () => groupByDate(data, (log) => log.CreatedDate),
    [data]
  )

  // Nothing selected at all: open the newest loaded record, since the pane is
  // most of the page and an empty one says nothing. Does not fire once a
  // `logId` is already set — including one that came in on a fresh link and
  // has not resolved yet — so it never overrides an in-flight selection.
  useEffect(() => {
    if (isLoading || !data.length || selectedLogId) return
    setSelectedLogId(String(data[0].ReportVideosLogId))
  }, [isLoading, data, selectedLogId, setSelectedLogId])

  /*
    Reset the selection when the filters change — but not on the first render,
    which would throw away a `logId` someone just followed. Seeding the ref with
    the current key makes the mount pass a no-op.
  */
  const filtersKey = `${activeSearchTerm}|${dateRange}|${dateFrom}|${dateTo}|${status}|${patientIssuer}`
  const previousFiltersKey = useRef(filtersKey)

  useEffect(() => {
    if (previousFiltersKey.current === filtersKey) return
    previousFiltersKey.current = filtersKey
    setSelectedLogId("")
  }, [filtersKey, setSelectedLogId])

  // A new record starts at the top of its own pane, not wherever the last one
  // was scrolled to.
  useEffect(() => {
    detailScrollRef.current?.scrollTo({ top: 0 })
  }, [selectedLogId])

  useEffect(() => {
    const sentinel = sentinelRef.current
    const root = sidebarScrollRef.current
    if (!sentinel || !root) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      // Rooted on the sidebar: the list scrolls in its own pane now, so
      // measuring against the viewport would fire at the wrong time or never.
      { root, rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleSelect = (log) => {
    setSelectedLogId(String(log.ReportVideosLogId))
    // Keeps arrow keys working after a click, without moving focus onto the row
    // itself — the list is the single tab stop.
    listRef.current?.focus()
  }

  const handleClearFilters = () => {
    setDateRange("All")
    setDateFrom("")
    setDateTo("")
    setStatus("All")
    setPatientIssuer("All")
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    // The page owns the viewport and scrolls its two panes instead of the
    // document. overflow-hidden is what actually stops the document scrolling:
    // without it, anything taller than the shell extends the body's scroll area
    // rather than being contained. The list and detail stay side by side at
    // every width — each pane keeps its own scroll rather than the page ever
    // switching to a stacked layout.
    <div className="flex h-page flex-col gap-3 overflow-hidden pb-4">
      <h1 className="shrink-0 text-2xl font-bold">Report Videos Logs</h1>

      <div className="shrink-0">
        <VideoLogsFilterBar
          dateFrom={dateFrom}
          dateRange={dateRange}
          dateTo={dateTo}
          isSearchActive={isSearchActive}
          issuerOptions={issuerOptions}
          patientIssuer={patientIssuer}
          searchTerm={searchTerm}
          showCustomDateRange={showCustomDateRange}
          status={status}
          onClearFilters={handleClearFilters}
          onClearSearch={handleClearSearch}
          onDateFrom={setDateFrom}
          onDateRange={setDateRange}
          onDateTo={setDateTo}
          onPatientIssuer={setPatientIssuer}
          onSearch={() => setActiveSearchTerm(searchTerm)}
          onSearchTerm={setSearchTerm}
          onStatus={setStatus}
        />
      </div>

      {/* min-h-0 is load-bearing: without it the sidebar's content sizes this
          row and the page grows past the viewport again. */}
      <div className="flex min-h-0 flex-1 gap-5">
        <LogsSidebar
          groups={groups}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          listRef={listRef}
          loadedCount={data.length}
          scrollRef={sidebarScrollRef}
          selectedId={selectedLogId}
          sentinelRef={sentinelRef}
          total={total}
          onLoadMore={fetchNextPage}
          onSelect={handleSelect}
        />

        <LogDetailPane
          detail={logDetail}
          isDetailLoading={isDetailLoading}
          isRowLoading={isRowLoading}
          log={activeRow}
          scrollRef={detailScrollRef}
          selectedId={selectedLogId}
          videos={videos}
        />
      </div>
    </div>
  )
}

export default EducationalVideoLogs
